import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Box,
  IconButton,
  TextField,
  Divider,
  InputAdornment,
  Typography,
  Button,
  Modal,
  Tooltip,
  Skeleton,
  Radio,
  RadioGroup,
  FormControl,
  FormControlLabel,
} from "@mui/material";
import { esignToolApi } from "../../../services/esignApi";
import { esignIcons } from "../../../components/Esign/icons";
import { toast } from "react-toastify";
import { useLogger } from "../../../hooks/useLogger";
import PdfThumbnail from "./PdfThumbnail";
import Loader from "../../../components/Loader";

const StampPaperTable = ({
  filteredDocs,
  fetchAllStampPapers,
  setPreviewOpen,
  previewOpen,
  page,
  setPage,
  rowsPerPage,
  setRowsPerPage,
  totalEntries,
  tableloader,
}) => {
  const logEvent = useLogger();
  const fileInputRef = React.useRef(null);
  const [loader, setLoader] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [stampPaperSuccess, setStampPaperSuccess] = useState(false);
  const [fadeOut, setFadeOut] = useState(false);
  const [date, setDate] = useState("");
  const [helperText, setHelperText] = useState({ date: "" });
  const [errors, setErrors] = useState({ date: "" });
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);

  const [editStatusOpen, setEditStatusOpen] = useState(false);
  const [stampIdToEdit, setStampIdToEdit] = useState(null);
  const [statusToEdit, setStatusToEdit] = useState(null);
  const [deleteStampPaperOpen, setDeletetampPaperOpen] = useState(false);

  // helpers ------------------------------------------------
  const toYYYYMMDD = (value) => {
    if (!value) return "";
    const d = new Date(value);
    return d.toISOString().split("T")[0];
  };

  const getTodayYYYYMMDD = () => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };
  const minDate = getTodayYYYYMMDD();

  const validateDate = () => {
    if (!date) {
      setErrors({ date: true });
      setHelperText({ date: "Expiry date is required" });
      return false;
    }
    const formatted = toYYYYMMDD(date);
    if (formatted < minDate) {
      setErrors({ date: true });
      setHelperText({ date: "Expiry date cannot be in the past" });
      return false;
    }
    setErrors({ date: "" });
    setHelperText({ date: "" });
    return true;
  };

  const handleDateChange = (e) => {
    const val = e.target.value;
    setDate(val);
    if (errors.date) {
      const formatted = toYYYYMMDD(val);
      if (val && formatted >= minDate) {
        setErrors({ date: "" });
        setHelperText({ date: "" });
      }
    }
  };

  const handleFileUpload = async (event) => {
    logEvent("Clicked", "Select File", "add signers");

    const file = event.target.files[0];
    if (!file) return;

    const allowedMimeTypes = [
      // "application/pdf",
      "application/zip",
      "application/x-zip-compressed",
    ];

    const allowedExtensions = ["zip"];

    const fileExtension = file.name.split(".").pop().toLowerCase();

    const isMimeValid = allowedMimeTypes.includes(file.type);
    const isExtValid = allowedExtensions.includes(fileExtension);

    if (!isMimeValid || !isExtValid) {
      toast.error("Please upload ZIP file only");
      event.target.value = null;
      return;
    }

    if (file.size > 50 * 1024 * 1024) {
      toast.error("ZIP file must be under 50MB");
      return;
    }
    setSelectedFile(file);

    event.target.value = null;
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const file = event.dataTransfer.files[0];
    if (!file) return;

    const isZip =
      file.type === "application/zip" ||
      file.type === "application/x-zip-compressed" ||
      file.name.toLowerCase().endsWith(".zip");

    if (!isZip) {
      toast.error("Only ZIP files are allowed");
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      toast.error("ZIP file must be under 50MB");
      return;
    }

    setSelectedFile(file);
  };

  const handleProceed = async () => {
    if (!selectedFile) return;
    if (!validateDate()) return;

    setLoader(true);
    try {
      const formData = new FormData();
      formData.append("Stamp_zip", selectedFile);
      formData.append("expired_date", toYYYYMMDD(date));

      const response = await esignToolApi.uploadStamps(formData);
      setSelectedFile(null);
      setLoader(false);
      if (response?.status) {
        setPreviewOpen(false);
        setStampPaperSuccess(true);
        fetchAllStampPapers();
        toast.success("File Uploaded SuccessFully");
        setTimeout(() => {
          setFadeOut(true);
          setTimeout(() => {
            setStampPaperSuccess(false);
            setFadeOut(false);
          }, 500);
        }, 1500);
      } else {
        setPreviewOpen(false);
        toast.warning(response?.message);
      }
    } catch (error) {
    //   toast.error("Upload failed!");
      console.error(error);
    } finally {
      setLoader(false);
    }
  };

  const formatFileSize = (bytes) => {
    const kb = bytes / 1024;
    const mb = kb / 1024;

    if (mb >= 1) {
      return `${Math.round(mb)} MB`;
    } else {
      return `${Math.round(kb)} KB`;
    }
  };

  const formatFileName = (name, maxLength = 20) => {
    if (!name) return "";

    if (name.length <= maxLength) return name;

    const dotIndex = name.lastIndexOf(".");
    const ext = dotIndex !== -1 ? name.slice(dotIndex) : "";
    const baseName =
      dotIndex !== -1 ? name.slice(0, dotIndex) : name;

    const startLength = maxLength - ext.length - 6; // 3 dots + 3 last chars
    const start = baseName.slice(0, Math.max(startLength, 1));
    const lastThree = baseName.slice(-3);

    return `${start}...${lastThree}${ext}`;
  };

  const handleDeleteStamp = async (stampId) => {
    try {
      if (!stampIdToEdit) return;
      setLoader(true);

      const payload = {
        Smtampid: stampIdToEdit.toString(),
      };

      const response = await esignToolApi.deleteStamp(payload);

      if (response?.status) {
        toast.success("Stamp deleted successfully");
        fetchAllStampPapers(); // refresh table
        setDeletetampPaperOpen(false);
      } else {
        toast.error(response?.message || "Delete failed");
      }
    } catch (error) {
      console.error("Delete Error:", error);
    //   toast.error("Something went wrong while deleting");
    } finally {
      setLoader(false);
    }
  };

  const getFileName = (url) => {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname;
    return pathname.substring(pathname.lastIndexOf("/") + 1);
  };

  const handleOpenStatusModal = (row) => {
    setStampIdToEdit(row.id);
    setStatusToEdit(row.status?.toUpperCase() || "UNUSED");
    setEditStatusOpen(true);
  };

  const handleStatusRadioChange = (e) => {
    setStatusToEdit(e.target.value);
  };

  const handleUpdateStatus = async () => {
    if (!stampIdToEdit) return;
    setLoader(true);

    const payload = {
      Smtampid: stampIdToEdit.toString(),
      status: statusToEdit,
    };

    try {
      const response = await esignToolApi.updateStampStatus(payload);

      if (response?.status) {
        toast.success("Status updated successfully");
        fetchAllStampPapers();
        setEditStatusOpen(false);
      } else {
        toast.error(response?.message || "Unable to update status");
      }
    } catch (err) {
      console.error(err);
    //   toast.error("Something went wrong while updating status");
    } finally {
      setLoader(false);
    }
  };

  return (
    <>
      {filteredDocs.length > 0 ? (
        <Paper
          sx={{
            width: "100%",
            overflow: "hidden",
            boxShadow: "none",
            height: "calc(100% - 141px)",
            borderRadius: "0 0 12px 12px",
          }}
        >
          <TableContainer sx={{ maxHeight: "88%" }}>
            <Table stickyHeader>
              <TableHead
                sx={{
                  background: "#F5F5F5",
                  fontSize: "15px",
                  fontWeight: 500,
                  color: "#1D1D1D",
                }}
              >
                <TableRow>
                  <TableCell sx={{ pl: "30px", background: "#F5F5F5" }}>
                    S. No.
                  </TableCell>
                  <TableCell sx={{ background: "#F5F5F5" }}>Name</TableCell>
                  <TableCell sx={{ background: "#F5F5F5" }}>Status</TableCell>
                  <TableCell sx={{ background: "#F5F5F5" }}>
                    Last Modified
                  </TableCell>
                  <TableCell sx={{ background: "#F5F5F5" }}>
                    Expiry Date
                  </TableCell>
                  <TableCell sx={{ background: "#F5F5F5" }}>Stamp</TableCell>
                  <TableCell sx={{ background: "#F5F5F5" }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody
                sx={{ color: "#202224", fontSize: "15px", fontWeight: 400 }}
              >
                {tableloader ? (
                  [...Array(rowsPerPage)].map((_, i) => (
                    <TableRow key={i}>
                      <TableCell sx={{ pl: "30px" }}>
                        <Skeleton variant="text" width={30} />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="text" width={180} />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="text" width={120} />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="text" width={80} />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="text" width={80} />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="text" width={80} />
                      </TableCell>
                      <TableCell>
                        <Skeleton
                          variant="circular"
                          width={30}
                          height={30}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  filteredDocs.length > 0 &&
                  [...filteredDocs].map((row, index) => (
                    <TableRow
                      key={row.id}
                      sx={{ "&:hover": { backgroundColor: "#f5f5f5" } }}
                    >
                      <TableCell
                        sx={{
                          pl: "30px",
                          cursor: "pointer",
                          maxWidth: "50px",
                        }}
                      >
                        {(page * rowsPerPage + index + 1)
                          .toString()
                          .padStart(2, "0")}
                      </TableCell>

                      <TableCell
                        sx={{
                          cursor: "pointer",
                          maxWidth: "150px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        title={row.name}
                      >
                        {getFileName(row.file)}
                      </TableCell>

                      <TableCell
                        sx={{
                          cursor: "pointer",
                          maxWidth: "200px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        title={row.status}
                      >
                        {row.status}
                      </TableCell>

                      <TableCell
                        sx={{
                          cursor: "pointer",
                          maxWidth: "130px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        title={row.last_modified}
                      >
                        {row.last_modified}
                      </TableCell>

                      <TableCell
                        sx={{
                          cursor: "pointer",
                          maxWidth: "150px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        title={row.expired_date}
                      >
                        {row.expired_date}
                      </TableCell>

                      <TableCell
                        sx={{
                          cursor: "pointer",
                          maxWidth: "150px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        title="Stamp Paper"
                      >
                        {row.file ? (
                          <PdfThumbnail
                            url={row.file}
                            onClick={() => setPdfPreviewUrl(row.file)}
                          />
                        ) : (
                          "-"
                        )}
                      </TableCell>

                      <TableCell
                        onClick={(e) => e.stopPropagation()}
                        sx={{ maxWidth: "100px" }}
                      >
                        {/* {row.status.toLowerCase() === "assigned" && (
                          <IconButton 
                            onClick={() => handleOpenStatusModal(row)}
                            disabled={loader}
                          >
                            {esignIcons.editIconBlue}
                          </IconButton>
                        )} */}
                        {row.status.toLowerCase() !== "used" && (
                          <IconButton
                            onClick={() => {setStampIdToEdit(row.id); setDeletetampPaperOpen(true);}}
                            disabled={loader}
                          >
                            {esignIcons.deleteIconRed}
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 20, 30, 50]}
            component="div"
            count={totalEntries}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </Paper>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            margin: "inherit",
          }}
        >
          <img
            src={`${process.env.PUBLIC_URL}/images/stampIcon.svg`}
            alt="stampIcon"
            style={{ height: 123 }}
          />
          <Typography
            variant="caption"
            sx={{
              fontSize: "20px",
              fontWeight: "600",
              color: "#000000",
              mt: "32px",
              mb: "37px",
            }}
          >
            No Stamp Paper Right now!
          </Typography>
          <Button
            variant="contained"
            sx={{
              minWidth: "152px",
              height: 40,
              color: "#ffffff",
              background: "#185CB7",
              textTransform: "none",
              fontSize: "14px",
              borderRadius: "10px",
              border: "1px solid #185CB7",
              boxShadow: "none",
              fontWeight: "500",
              display: "flex",
              p: "9px 11px",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "4px",
            }}
            onClick={() => {
              setPreviewOpen(true);
            }}
          >
            <svg width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M4.38855 7.19727C3.7552 7.20195 3.37408 7.22136 3.07052 7.30174C1.6889 7.66758 0.742996 8.90585 0.780236 10.2999C0.791023 10.7037 0.94474 11.2028 1.25217 12.2009C1.99205 14.603 3.2355 16.6883 6.02108 17.1886C6.53313 17.2806 7.10931 17.2806 8.26169 17.2806L9.79666 17.2806C10.949 17.2806 11.5252 17.2806 12.0373 17.1886C14.8228 16.6883 16.0663 14.603 16.8062 12.2009C17.1136 11.2028 17.2673 10.7037 17.2781 10.2999C17.3154 8.90585 16.3695 7.66758 14.9878 7.30174C14.6843 7.22136 14.3032 7.20195 13.6698 7.19727" stroke="white" stroke-width="1.55833" stroke-linecap="round"/>
            <path d="M9.0292 0.779948L9.0292 10.8633M9.0292 0.779948C9.45854 0.779948 9.78489 1.18154 10.4376 1.98472L11.3209 3.07161M9.0292 0.779948C8.59985 0.779948 8.2735 1.18154 7.62079 1.98472L6.73753 3.07161" stroke="white" stroke-width="1.55833" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            Upload Stamp
          </Button>
        </Box>
      )}

      <Modal open={previewOpen} onClose={() => setPreviewOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "720px",
            minHeight: "480px",
            backgroundColor: "#fff",
            boxShadow: 24,
            overflow: "auto",
            borderRadius: "20px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: "20px 30px",
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontSize: "22px", fontWeight: 600, color: "#1D1D1D" }}
            >
              Upload Stamp
            </Typography>
          </Box>
          <Divider sx={{ borderWidth: "1px", margin: 0, padding: 0 }} />

          <Box sx={{ p: "30px", display: "flex" }}>
            <Box sx={{ width: "50%" }}>
              <Typography
                sx={{
                  fontSize: "18px",
                  fontWeight: 600,
                  color: "#1D1D1D",
                }}
              >
                Please select the expiration date for DDPI Stamps
              </Typography>
              <TextField
                placeholder="Select Date"
                name="date"
                type="date"
                variant="outlined"
                fullWidth={false}
                value={date}
                onChange={handleDateChange}
                error={!!errors.date}
                helperText={helperText.date}
                sx={{
                  boxSizing: "border-box",
                  borderRadius: "10px",
                  backgroundColor: "#fff",
                  width: "95%",
                  "& .MuiOutlinedInput-root": {
                    borderColor: errors.date ? "#FF6666" : "##E0E0E0",
                    backgroundColor: errors.date ? "#FFF1F1" : "none",
                    "&:hover fieldset": {
                      borderColor: errors.date
                        ? "#FF6666"
                        : "##E0E0E0",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: errors.date
                        ? "#FF6666"
                        : "##E0E0E0",
                    },
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <img
                        loading="lazy"
                        src={`${process.env.PUBLIC_URL}/images/calendar-03.svg`}
                        alt="Calendar Icon"
                        style={{ width: 20, height: 20, cursor: "pointer" }}
                        onClick={() => {
                          document
                            .getElementById("custom-date-input")
                            .showPicker?.();
                        }}
                      />
                    </InputAdornment>
                  ),
                  sx: {
                    borderRadius: "10px",
                    height: 45,
                    fontSize: "16px",
                    fontWeight: date ? "600" : "400",
                    mt: "29px",
                  },
                }}
                inputProps={{
                  id: "custom-date-input",
                  min: minDate,
                }}
              />
            </Box>
            <Box sx={{ width: "50%" }}>
              <Box style={{ display: "flex", flexDirection: "column", border: "1px dashed #BDBDBD", borderRadius: "12px", background: "#F6F6F6", padding: "29px 19px" }}>
                { !selectedFile ? (
                  <Box 
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    // onMouseEnter={() => setIsHovered(true)}
                    // onMouseLeave={() => setIsHovered(false)}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: 'center',
                      cursor: 'pointer',
                      gap: "17px",
                    }}
                    onClick={() => fileInputRef.current?.click()}>
                    <Box component="img" src={`${process.env.PUBLIC_URL}/images/uploadIcon.svg`} alt="uploadIcon" height="96px" />
                    <Typography variant="p" sx={{ background: 'transparent', color:'#1D1D1D', fontSize: '18px', fontWeight: '600' }}>Upload File</Typography>

                    <Typography variant="p" sx={{ background: 'transparent', fontSize: '12px', color: '#A3A3A3', fontWeight: '400' }}>Supported file .zip only (Max Size 50Mb)</Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: "flex", borderRadius: "12px", background: "#FFFFFF", padding: "33px 35px", height: "160px", gap: "12px" }}>
                    <Box component="img" src={`${process.env.PUBLIC_URL}/images/${selectedFile?.type == "application/pdf" ? 'pdf-file' : 'zip'}.svg`} alt="zip" height="95px" />
                    <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
                      <Tooltip title={selectedFile?.name || ""} arrow placement="top">
                        <Typography variant="p" sx={{ background: 'transparent', color:'#000000', fontSize: '16px', fontWeight: '600', overflowWrap: "anywhere" }}>{formatFileName(selectedFile?.name, 30)}</Typography>
                      </Tooltip>
                      <Typography variant="p" sx={{ background: 'transparent', color:'#797777', fontSize: '13px', fontWeight: '400' }}>File size - {selectedFile ? formatFileSize(selectedFile.size) : ""}</Typography>
                    </Box>
                  </Box>
                )}
                <Button variant="contained" sx={{ mt: "28px", width: '180px', height: 42, color: "#185CB7", background: "#FFFFFF", textTransform: "none", fontSize: "16px", borderRadius: "10px", border: "1px solid #185CB7", boxShadow: "none", fontWeight: "500", "&:hover": { background: "#FFFFFF" }, alignSelf: "center" }} onClick={() => fileInputRef.current?.click()}>{selectedFile ? 'Change' : 'Browse'}</Button>
                <input type="file" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileUpload} accept=".zip,application/zip,application/x-zip-compressed" />                            
              </Box>
            </Box>
          </Box>
          <Divider sx={{ borderWidth: "1px", }} />
          <Box sx={{ p: "0 30px", mb: "15px" }}>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: "15px", gap: 2 }}>
              <Button variant="contained" sx={{ width: '130px', height: 46, color: "#1D1D1D", background: "transparent", textTransform: "none", fontSize: "16px", borderRadius: "10px", border: "1px solid #D2D2D2", boxShadow: "none", fontWeight: "500", }}  onClick={() => {setPreviewOpen(false);}} >
                Cancel
              </Button>
              <Button variant="contained" sx={{ width: '155px', height: 46, color: "#ffffff", background: "#185CB7", textTransform: "none", fontSize: "16px", borderRadius: "10px", border: "1px solid #185CB7", boxShadow: "none", fontWeight: "500", "&:disabled": {background: "#E4E4E4", color: "#ffffff", borderColor: "#E4E4E4"} }} onClick={handleProceed} >
                Save
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      <Modal open={stampPaperSuccess} onClose={() => setStampPaperSuccess(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "528px",
            height: "320px",
            backgroundColor: "#fff",
            boxShadow: 24,
            overflow: "auto",
            borderRadius: "20px",
            display: "flex",
            flexDirection: "column",
            opacity: fadeOut ? 0 : 1,
            transition: "opacity 0.5s ease",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: "1em 1em  1em 2em" }}>
            <Typography variant="h6" sx={{ fontSize: "22px", fontWeight: 600, color: "#1D1D1D" }}>Stamp Papers</Typography>
          </Box>
          <Divider sx={{ borderWidth : "1px", margin: 0, padding: 0 }} />
          <Box sx={{ p: "0 30px 0 30px", width: "100%", margin: "auto" }}>
            <Box display="flex" justifyContent="center">
              <svg width="85" height="85" viewBox="0 0 85 85" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M85 42.5C85 65.9721 65.9721 85 42.5 85C19.0279 85 0 65.9721 0 42.5C0 19.0279 19.0279 0 42.5 0C65.9721 0 85 19.0279 85 42.5Z" fill="#17B26A"/>
              <path d="M63.75 29.75L49.431 45.7435C43.7569 52.0812 40.9199 55.25 37.1875 55.25C33.4551 55.25 30.6181 52.0812 24.944 45.7435L21.25 41.6175" stroke="white" stroke-width="4" stroke-linecap="round"/>
              </svg>
            </Box>
            <Typography sx={{ m: "auto", background: 'transparent', color:'#3D3D3D', fontSize: '18px', fontWeight: '500', textAlign: "center", mt: "20px", }}>All your stamp papers have been uploaded successfully.</Typography>
          </Box>
        </Box>
      </Modal>

      <Modal open={Boolean(pdfPreviewUrl)} onClose={() => setPdfPreviewUrl(null)}>
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0,0,0,0.15)",
            zIndex: 1300,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              width: { xs: "98vw", sm: "80vw", md: "60vw" },
              height: { xs: "90vh", sm: "85vh", md: "80vh" },
              backgroundColor: "#fff",
              borderRadius: "12px",
              boxShadow: 24,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: 2,
                borderBottom: "1px solid #E0E0E0",
              }}
            >
              <Typography fontWeight={600}>Stamp Preview</Typography>
              <Button sx={{ minWidth: "auto", padding: "0", "&:hover": { background: "none", } }} onClick={() => setPdfPreviewUrl(null)}>
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M29.3337 15.9997C29.3337 8.63588 23.3641 2.66634 16.0003 2.66634C8.63653 2.66634 2.66699 8.63588 2.66699 15.9997C2.66699 23.3635 8.63653 29.333 16.0003 29.333C23.3641 29.333 29.3337 23.3635 29.3337 15.9997Z" fill="#1D1D1D"/>
                <path d="M19.9991 20L12 12M12.0009 20L20 12" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </Button>
            </Box>
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <iframe
                src={pdfPreviewUrl}
                title="PDF Preview"
                width="100%"
                height="100%"
                style={{ border: "none", display: "block", minHeight: "100%", minWidth: "100%" }}
                allowFullScreen
              />
            </Box>
          </Box>
        </Box>
      </Modal>

      <Modal open={editStatusOpen} onClose={() => setEditStatusOpen(false)}>
        <Box
            sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: "555px",
                maxHeight: "600px",
                backgroundColor: "#fff",
                boxShadow: 24,
                overflow: "auto",
                borderRadius: "20px",
            }}
        >
        <Box
            sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: "20px 30px",
            }}
        >
            <Typography variant="h6" sx={{ fontSize: "22px", fontWeight: 600, color: "#1D1D1D" }}>
                Change Stamp Status
            </Typography>
        </Box>
        <Divider />
        <Box sx={{ padding: "30px 28px" }}>
            <FormControl component="fieldset">
            <RadioGroup
                value={statusToEdit}
                onChange={handleStatusRadioChange}
                sx={{ display: "flex", flexDirection: "row", gap: 4 }}
            >
                <FormControlLabel value="UNUSED" control={<Radio />} label="Unused"/>
                <FormControlLabel value="USED" control={<Radio />} label="Used" />
                <FormControlLabel value="ASSIGNED" control={<Radio />} label="Assigned" />
            </RadioGroup>
            </FormControl>
        </Box>
        <Divider sx={{ borderWidth: "1px", margin: 0, padding: 0 }} />
        <Box sx={{ p: "0 30px", mb: 3 }}>
            <Box
            sx={{ display: "flex", justifyContent: "flex-end", mt: 3, gap: 2 }}
            >
            <Button
                variant="contained"
                sx={{
                width: "130px",
                height: 46,
                color: "#1D1D1D",
                background: "transparent",
                textTransform: "none",
                fontSize: "16px",
                borderRadius: "10px",
                border: "1px solid #D2D2D2",
                boxShadow: "none",
                fontWeight: "500",
                }}
                onClick={() => setEditStatusOpen(false)}
            >
                Cancel
            </Button>
            <Button
                variant="contained"
                sx={{
                width: "155px",
                height: 46,
                color: "#ffffff",
                background: "#185CB7",
                textTransform: "none",
                fontSize: "16px",
                borderRadius: "10px",
                border: "1px solid #185CB7",
                boxShadow: "none",
                fontWeight: "500",
                "&:disabled": {
                    background: "#E4E4E4",
                    color: "#ffffff",
                    borderColor: "#E4E4E4",
                },
                }}
                onClick={handleUpdateStatus}
                disabled={loader}
            >
                Update
            </Button>
            </Box>
        </Box>
        </Box>
      </Modal>

      <Modal open={deleteStampPaperOpen} onClose={() => setDeletetampPaperOpen(false)}>
        <Box
            sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: "434px",
                height: "368px",
                backgroundColor: "#fff",
                boxShadow: 24,
                overflow: "auto",
                borderRadius: "20px",
            }}
        >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: "1em 1em  1em 2em" }}>
                <Typography variant="h6" sx={{ fontSize: "22px", fontWeight: 600, color: "#1D1D1D" }}>Delete Stamp Paper</Typography>
            </Box>
            <Divider sx={{ borderWidth : "1px", margin: 0, padding: 0 }} />
            <Box sx={{ p: "1em 2em", mb: 3, display: "flex", flexDirection: "column", justifyContent: "space-between", height: "74%", gap: 3 }}>
                <Box>
                    <img src={`${process.env.PUBLIC_URL}/images/delete_stamp.svg`} alt="delete" style={{ height: 58 }} />
                </Box>
                <Typography variant="p" sx={{ fontSize: "20px", fontWeight: 400, color: "#1D1D1D" }}>Are you sure you want to delete this Stamp Paper?</Typography>
                <Box sx={{ display: "flex", justifyContent: "space-between", mt: "auto", gap: 2 }}>
                    <Button variant="contained" sx={{ width: '171px', height: 46, color: "#1D1D1D", background: "transparent", textTransform: "none", fontSize: "16px", borderRadius: "10px", border: "1px solid #D2D2D2", boxShadow: "none", fontWeight: "500",}}  onClick={() => {setDeletetampPaperOpen(false);}} >
                        No
                    </Button>
                    <Button variant="contained" sx={{ width: '171px', height: 46, color: "#ffffff", background: "#C5060B", textTransform: "none", fontSize: "16px", borderRadius: "10px", border: "1px solid #C5060B", boxShadow: "none", fontWeight: "500",}} onClick={handleDeleteStamp}  >
                        Yes
                    </Button>
                </Box>
            </Box>
        </Box>
    </Modal>
      {loader && <Loader />}
    </>
  );
};

export default StampPaperTable;
